import { ShieldCheck } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/api/useAuth';
import { useElevatedAuth } from '../../hooks/api/useElevatedAuth';
import { useAuth } from '../../hooks/useAuth';
import { MAX_SUPERADMIN_AGE_SECONDS } from '../../lib/constants';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

// Helper function to check if JWT is older than the superadmin age limit
function isJWTTooOldForSuperadmin(jwtAge: number): boolean {
  return jwtAge > MAX_SUPERADMIN_AGE_SECONDS;
}

interface ElevatedRouteProps {
  children: React.ReactNode;
}

export function ElevatedRoute({ children }: ElevatedRouteProps) {
  const { user, openLogin, isLoading } = useAuth();
  const { data: userProfile } = useCurrentUser();
  const { elevate, isElevating, isElevated, error } = useElevatedAuth();
  const navigate = useNavigate();

  // Wait for auth to load
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If no user (auth failed/expired), show login modal
  if (!user) {
    // Auto-open login modal
    if (!isLoading) {
      openLogin({
        redirectOnCancel: '/ai',
        title: 'Authentication Required',
        description: 'Please sign in to access superadmin features',
      });
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-card p-6 rounded-lg max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-muted-foreground mb-4">
            Please sign in to access this page. A login dialog should open automatically.
          </p>
          <button
            type="button"
            onClick={() =>
              openLogin({
                redirectOnCancel: '/ai',
                title: 'Authentication Required',
                description: 'Please sign in to access superadmin features',
              })
            }
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Not a superadmin
  if (!user?.roles.includes('superadmin')) {
    return <Navigate to="/ai" />;
  }

  // Already elevated
  if (isElevated) {
    return <>{children}</>;
  }

  // Check if JWT is older than allowed age - if so, require fresh login
  const needsFreshLogin = userProfile ? isJWTTooOldForSuperadmin(userProfile.jwtAge) : true;

  if (needsFreshLogin) {
    const handleFreshLogin = () => {
      // Force login modal to show even though user is authenticated
      // Keep the JWT - it's valid for non-superadmin areas
      openLogin({
        redirectOnCancel: '/ai', // Go to AI if they cancel
        title: 'Fresh Authentication Required',
        description:
          'For security, superadmin access requires authentication within the last hour. Please sign in again.',
        force: true, // Force modal to show even with valid user
      });
    };

    const handleGoBack = () => {
      // Check if we have history and if the previous page wasn't a superadmin page
      const referrer = document.referrer;
      const canGoBack = referrer && !referrer.includes('/superadmin');

      if (canGoBack && window.history.length > 1) {
        window.history.back();
      } else {
        // No safe history, go to AI page
        navigate('/ai');
      }
    };

    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <ShieldCheck className="h-12 w-12 text-primary" />
            </div>
            <CardTitle>Fresh Authentication Required</CardTitle>
            <CardDescription>
              For security, superadmin access requires authentication within the last day. Please
              sign in again to access elevated features.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleFreshLogin} className="w-full">
              Sign In Again
            </Button>

            <div className="text-center">
              <Button variant="link" onClick={handleGoBack}>
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show elevation prompt (JWT is fresh, allow elevation)
  const handleGoBack = () => {
    // Check if we have history and if the previous page wasn't a superadmin page
    const referrer = document.referrer;
    const canGoBack = referrer && !referrer.includes('/superadmin');

    if (canGoBack && window.history.length > 1) {
      window.history.back();
    } else {
      // No safe history, go to AI page
      navigate('/ai');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <ShieldCheck className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>Elevated Access Required</CardTitle>
          <CardDescription>
            To access the superadmin panel, you need to re-authenticate for security purposes.
            Elevated access expires after 15 minutes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={() => elevate()} disabled={isElevating} className="w-full">
            {isElevating ? 'Authenticating...' : 'Authenticate for Elevated Access'}
          </Button>

          {error && <div className="text-sm text-destructive text-center">{error.message}</div>}

          <div className="text-center">
            <Button variant="link" onClick={handleGoBack}>
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
